import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import ChatMessage from './components/ChatMessage';
import QuickActionButtons from './components/QuickActionButtons';
import ConversationHistory from './components/ConversationHistory';
import { useAuth } from '../../contexts/AuthContext';
import { chatbotService } from '../../services/chatbotService';

const AIFitnessAssistantChatbot = () => {
  const navigate = useNavigate();
  const { user, currentTenant } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (currentTenant?.id && user?.id) {
      loadConversations();
      startNewConversation();
    }
  }, [currentTenant, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    const result = await chatbotService?.getUserConversations(user?.id, currentTenant?.id);
    if (result?.success) {
      setConversations(result?.data || []);
    }
  };

  const startNewConversation = async () => {
    const result = await chatbotService?.createConversation(
      user?.id,
      currentTenant?.id,
      'New Fitness Chat'
    );
    if (result?.success) {
      setCurrentConversationId(result?.conversationId);
      setMessages([]);
    }
  };

  const loadConversation = async (conversationId) => {
    setCurrentConversationId(conversationId);
    const history = await chatbotService?.getConversationHistory(
      conversationId,
      user?.id,
      currentTenant?.id
    );
    
    const formattedMessages = history?.map(msg => ({
      role: msg?.role,
      content: msg?.content,
      timestamp: new Date(msg?.created_at)
    }));
    
    setMessages(formattedMessages);
    setShowHistory(false);
  };

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || loading) return;

    const userMessage = inputMessage?.trim();
    setInputMessage('');
    
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      const result = await chatbotService?.getChatbotResponse(
        userMessage,
        currentConversationId,
        user?.id,
        currentTenant?.id
      );

      if (result?.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: result?.response,
          timestamp: new Date()
        }]);

        await loadConversations();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I apologize, but I encountered an error: ${result?.error}. Please try again.`,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an unexpected error. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (prompt) => {
    setInputMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    const result = await chatbotService?.deleteConversation(
      conversationId,
      user?.id,
      currentTenant?.id
    );
    if (result?.success) {
      await loadConversations();
      if (conversationId === currentConversationId) {
        startNewConversation();
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-6 pb-24 md:pb-6 flex flex-col">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              AI Fitness Assistant
            </h1>
            <p className="text-muted-foreground">
              Get personalized fitness guidance and support
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowHistory(!showHistory)}
              variant="outline"
              iconName="History"
              iconSize={20}
              className="hidden md:flex"
            >
              History
            </Button>
            <Button
              onClick={startNewConversation}
              iconName="Plus"
              iconSize={20}
            >
              New Chat
            </Button>
          </div>
        </div>

        <div className="flex-1 flex gap-4 overflow-hidden">
          {/* Conversation History Sidebar */}
          {showHistory && (
            <div className="hidden md:block w-80 bg-card border border-border rounded-lg shadow-md overflow-hidden">
              <ConversationHistory
                conversations={conversations}
                currentConversationId={currentConversationId}
                onSelectConversation={loadConversation}
                onDeleteConversation={handleDeleteConversation}
              />
            </div>
          )}

          {/* Chat Interface */}
          <div className="flex-1 bg-card border border-border rounded-lg shadow-md flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
              {messages?.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon name="Bot" size={40} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
                    Welcome to Your AI Fitness Assistant!
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl">
                    I'm here to help with workout modifications, nutrition advice, exercise form guidance, 
                    motivation, and any questions about gym services. How can I assist you today?
                  </p>
                  <QuickActionButtons onSelectPrompt={handleQuickAction} />
                </div>
              ) : (
                <>
                  {messages?.map((message, index) => (
                    <ChatMessage key={index} message={message} />
                  ))}
                  {loading && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>AI is thinking...</span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <div className="border-t border-border p-4 bg-muted/30">
              {messages?.length > 0 && (
                <div className="mb-3">
                  <QuickActionButtons onSelectPrompt={handleQuickAction} compact />
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about workouts, nutrition, form checks, or anything fitness-related..."
                  rows={2}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage?.trim() || loading}
                  iconName="Send"
                  iconSize={20}
                  className="self-end"
                  aria-label="Send message"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AIFitnessAssistantChatbot;